import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { useSelector } from 'react-redux'
import BackHeader from '../../components/BackHeader/BackHeader'
import Button from '../../components/Button/Button'
import Card from '../../components/Card/Card'
import ComboInput from '../../components/ComboInput'
import Modal from '../../components/Modal/Modal'
import Switch from '../../components/Switch/Switch'
import Notification from '../../components/Notification/Notification'
import PullToRefresh from '../../components/PullToRefresh/PullToRefresh'
import {
  WORLD_CUP_LEADERBOARD,
  WORLD_CUP_LEAGUES,
  WORLD_CUP_MATCHES,
  WORLD_CUP_MATCH_GUESSES,
  WORLD_CUP_STAGES,
  MY_WORLD_CHAMPION_GUESS,
  MY_WORLD_CUP_GUESSES,
  WORLD_CUP_TEAMS,
} from '../../graphql/queries'
import {
  CREATE_WORLD_CUP_GUESS,
  CREATE_WORLD_CUP_LEAGUE,
  JOIN_WORLD_CUP_LEAGUE,
  LEAVE_WORLD_CUP_LEAGUE,
  SAVE_WORLD_CHAMPION_GUESS,
} from '../../graphql/mutations'
import WorldCupGroupsView from './WorldCupGroupsView'
import WorldCupMatchesView from './WorldCupMatchesView'
import WorldCupMyGuessesView from './WorldCupMyGuessesView'
import WorldCupRulesModal from './WorldCupRulesModal'
import WorldCupTeamFlag from './WorldCupTeamFlag'
import { sortWorldCupMatchesByKickoff } from './worldCupMatchSections'
import {
  buildMissingGuessesBannerText,
  getMissingGuesses,
  getMissingGuessesWithinHours,
} from './worldCupMissingGuesses'
import { worldCupStageLabel } from './worldCupTeamFlags'
import { DEFAULT_STAGES } from '../AdminPanel/WorldCupAdmin/worldCupAdmin.constants'
import Loading from '../../components/Loading/Loading'
import './WorldCup2026.css'

const tabs = [
  { key: 'teams', label: 'Hold', path: '/world-cup-2026/teams' },
  { key: 'matches', label: 'Kampe', path: '/world-cup-2026/matches' },
  { key: 'my-guesses', label: 'Mine gæt', path: '/world-cup-2026/my-guesses' },
  { key: 'leaderboard', label: 'Leaderboard', path: '/world-cup-2026/leaderboard' },
  { key: 'leagues', label: 'Grupper', path: '/world-cup-2026/leagues' },
  { key: 'world-champion', label: 'Verdensmester', path: '/world-cup-2026/world-champion' },
]

const fmtKickoffDate = (iso) => new Intl.DateTimeFormat('da-DK', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
}).format(new Date(iso))

const WORLD_CHAMPION_POINTS = 10

const isInitialQueryLoading = (loading, data) => loading && data === undefined

function WorldCupTabLoading({ text = 'Henter…' }) {
  return (
    <Card className="worldcup-panel-card worldcup-tab-loading" hover={false}>
      <Loading text={text} />
    </Card>
  )
}

function WorldCupLeaderboardRefreshButton({ onRefresh, title = 'Hent seneste point og stilling' }) {
  const [isRefreshing, setIsRefreshing] = useState(false)

  const handleRefresh = async () => {
    if (!onRefresh || isRefreshing) return
    setIsRefreshing(true)
    try {
      await onRefresh()
    } finally {
      setIsRefreshing(false)
    }
  }

  return (
    <button
      type="button"
      className="leaderboard-refresh-button"
      onClick={handleRefresh}
      disabled={isRefreshing}
      title={title}
      aria-label={title}
    >
      {isRefreshing ? (
        <Loading size="small" variant="refresh" inline text="" />
      ) : (
        <span className="leaderboard-refresh-button-icon">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
              d="M21 12a9 9 0 1 1-3.3-6.9"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points="21 3 21 9 15 9"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
    </button>
  )
}

export default function WorldCup2026() {
  const navigate = useNavigate()
  const location = useLocation()
  const client = useApolloClient()
  const [toast, setToast] = useState({ open: false, message: '', type: 'info' })
  const [guessModal, setGuessModal] = useState({ open: false, match: null })
  const [form, setForm] = useState({ homeGoals: '', awayGoals: '', isJoker: false })
  const [leagueForm, setLeagueForm] = useState({ name: '', code: '' })
  const [championTeam, setChampionTeam] = useState('')
  const [editingChampionGuess, setEditingChampionGuess] = useState(false)
  const [selectedMatchId, setSelectedMatchId] = useState(null)
  const [selectedLeagueId, setSelectedLeagueId] = useState(null)
  const [leagueModalOpen, setLeagueModalOpen] = useState(false)
  const [leaveLeagueConfirm, setLeaveLeagueConfirm] = useState(null)
  const [jokerInfoOpen, setJokerInfoOpen] = useState(false)
  const [rulesOpen, setRulesOpen] = useState(false)
  const [leaderboardPlayerModal, setLeaderboardPlayerModal] = useState(null)
  const tabsRef = useRef(null)
  const currentUserId = useSelector((state) => state.mate.currentUser?.id)

  useEffect(() => {
    if (location.pathname === '/world-cup-2026' || location.pathname === '/world-cup-2026/') {
      navigate('/world-cup-2026/teams', { replace: true })
    }
  }, [location.pathname, navigate])

  const activeTab = useMemo(() => {
    const hit = tabs.find((t) => location.pathname === t.path)
    if (hit) return hit.key
    if (location.pathname === '/world-cup-2026' || location.pathname === '/world-cup-2026/') {
      return 'teams'
    }
    return 'matches'
  }, [location.pathname])

  useEffect(() => {
    const container = tabsRef.current
    if (!container) return
    const activeButton = container.querySelector('button.active')
    activeButton?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
  }, [activeTab])

  const tabsNeedingMatchSchedule = ['matches', 'my-guesses', 'world-champion', 'leaderboard', 'leagues']
  const needsTeams = ['teams', ...tabsNeedingMatchSchedule].includes(activeTab)
  const needsStages = activeTab === 'matches'
  const needsMatches = tabsNeedingMatchSchedule.includes(activeTab)
  // Hent egne gæt sammen med kampe — ellers nulstilles badge/banner på Kampe-fanen på andre faner
  const needsMyGuesses = needsMatches
  const needsChampion = activeTab === 'world-champion'
  const needsLeaderboard = activeTab === 'leaderboard'
  const needsLeagues = activeTab === 'leagues'

  const { data: matchesData, loading: matchesLoading, refetch: refetchMatches } = useQuery(WORLD_CUP_MATCHES, {
    skip: !needsMatches,
    fetchPolicy: 'cache-and-network',
  })
  const { data: myGuessesData, loading: myGuessesLoading, refetch: refetchMyGuesses } = useQuery(MY_WORLD_CUP_GUESSES, {
    skip: !needsMyGuesses,
    fetchPolicy: 'cache-and-network',
  })
  const { data: leaderboardData, loading: leaderboardLoading, refetch: refetchLeaderboard } = useQuery(WORLD_CUP_LEADERBOARD, {
    skip: !needsLeaderboard,
    fetchPolicy: 'cache-and-network',
  })
  const { data: leaguesData, loading: leaguesLoading, refetch: refetchLeagues } = useQuery(WORLD_CUP_LEAGUES, {
    skip: !needsLeagues,
    fetchPolicy: 'cache-and-network',
  })
  const { data: championData, loading: championLoading, refetch: refetchChampion } = useQuery(MY_WORLD_CHAMPION_GUESS, {
    skip: !needsChampion,
    fetchPolicy: 'cache-and-network',
  })
  const { data: teamsData, loading: teamsLoading, error: teamsError, refetch: refetchTeams } = useQuery(WORLD_CUP_TEAMS, {
    skip: !needsTeams,
    fetchPolicy: 'cache-and-network',
  })
  const { data: matchGuessesData } = useQuery(WORLD_CUP_MATCH_GUESSES, {
    variables: { matchId: selectedMatchId },
    skip: !selectedMatchId,
    fetchPolicy: 'cache-and-network',
  })
  const { data: stagesData, loading: stagesLoading } = useQuery(WORLD_CUP_STAGES, {
    skip: !needsStages,
    fetchPolicy: 'cache-and-network',
  })

  const matches = useMemo(() => {
    const list = Array.isArray(matchesData?.worldCupMatches) ? matchesData.worldCupMatches : []
    return sortWorldCupMatchesByKickoff(list)
  }, [matchesData])
  const stages = useMemo(() => {
    const fromDb = stagesData?.worldCupStages
    if (Array.isArray(fromDb) && fromDb.length > 0) {
      return [...fromDb].sort((a, b) => a.sortOrder - b.sortOrder)
    }
    return DEFAULT_STAGES
  }, [stagesData])
  const myGuesses = useMemo(
    () => (Array.isArray(myGuessesData?.myWorldCupGuesses) ? myGuessesData.myWorldCupGuesses : []),
    [myGuessesData]
  )
  const leaderboard = useMemo(
    () => (Array.isArray(leaderboardData?.worldCupLeaderboard) ? leaderboardData.worldCupLeaderboard : []),
    [leaderboardData]
  )
  const leagues = useMemo(() => {
    const list = Array.isArray(leaguesData?.worldCupLeagues) ? leaguesData.worldCupLeagues : []
    return [...list].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  }, [leaguesData])
  const activeLeague = useMemo(() => {
    if (!leagues.length) return null
    return leagues.find((l) => l.id === selectedLeagueId) || leagues[0]
  }, [leagues, selectedLeagueId])

  useEffect(() => {
    if (!leagues.length) {
      setSelectedLeagueId(null)
      return
    }
    setSelectedLeagueId((current) => {
      if (current && leagues.some((l) => l.id === current)) return current
      return leagues[0].id
    })
  }, [leagues])
  const myChampionGuess = championData?.myWorldChampionGuess || null
  const teams = useMemo(
    () => (Array.isArray(teamsData?.worldCupTeams?.teams) ? teamsData.worldCupTeams.teams : []),
    [teamsData]
  )
  const teamOptions = useMemo(() => {
    if (teams.length === 0) return []
    return teams.map((t) => t.name).filter(Boolean).sort((a, b) => a.localeCompare(b, 'da-DK'))
  }, [teams])
  const flagCodeByName = useMemo(() => {
    const map = {}
    teams.forEach((t) => {
      if (t?.name) map[t.name] = t.flagCode
    })
    return map
  }, [teams])
  const selectedMatchGuesses = Array.isArray(matchGuessesData?.worldCupMatchGuesses) ? matchGuessesData.worldCupMatchGuesses : []

  const myGuessByMatch = useMemo(() => {
    const out = {}
    myGuesses.forEach((g) => { out[g.matchId] = g })
    return out
  }, [myGuesses])

  const missingGuessSummary = useMemo(() => {
    const missingMatches = getMissingGuesses(matches, myGuessByMatch)
    const urgentMatches = getMissingGuessesWithinHours(matches, myGuessByMatch, 12)
    return {
      totalMissing: missingMatches.length,
      urgentMissing: urgentMatches.length,
      bannerText: buildMissingGuessesBannerText({
        totalMissing: missingMatches.length,
        urgentMissing: urgentMatches.length,
      }),
      isUrgent: urgentMatches.length > 0,
    }
  }, [matches, myGuessByMatch])

  const jokerUsed = myGuesses.filter((g) => g.isJoker).length

  const canSaveGuess = useMemo(() => {
    if (form.homeGoals === '' || form.awayGoals === '') return false
    const h = Number(form.homeGoals)
    const a = Number(form.awayGoals)
    return Number.isInteger(h) && h >= 0 && Number.isInteger(a) && a >= 0
  }, [form.homeGoals, form.awayGoals])

  const [createGuess, { loading: savingGuess }] = useMutation(CREATE_WORLD_CUP_GUESS, {
    onCompleted: async () => {
      setToast({ open: true, message: 'Gæt gemt og låst', type: 'success' })
      setGuessModal({ open: false, match: null })
      await Promise.all([refetchMyGuesses(), refetchMatches(), refetchLeaderboard(), refetchLeagues()])
    },
    onError: (e) => setToast({ open: true, message: e.message || 'Kunne ikke gemme gæt', type: 'error' }),
  })

  const [saveChampion, { loading: savingChampion }] = useMutation(SAVE_WORLD_CHAMPION_GUESS, {
    onCompleted: async () => {
      setToast({ open: true, message: 'Verdensmester-gæt gemt', type: 'success' })
      setEditingChampionGuess(false)
      await Promise.all([refetchChampion(), refetchLeaderboard(), refetchLeagues()])
    },
    onError: (e) => setToast({ open: true, message: e.message || 'Kunne ikke gemme verdensmester', type: 'error' }),
  })

  const [createLeague] = useMutation(CREATE_WORLD_CUP_LEAGUE, {
    onCompleted: async (data) => {
      const created = data?.createWorldCupLeague
      setToast({ open: true, message: 'Gruppe oprettet', type: 'success' })
      setLeagueForm((p) => ({ ...p, name: '' }))
      setLeagueModalOpen(false)
      if (created?.id) setSelectedLeagueId(created.id)
      await refetchLeagues()
    },
    onError: (e) => setToast({ open: true, message: e.message || 'Kunne ikke oprette gruppe', type: 'error' }),
  })
  const [joinLeague] = useMutation(JOIN_WORLD_CUP_LEAGUE, {
    onCompleted: async (data) => {
      const joined = data?.joinWorldCupLeague
      setToast({
        open: true,
        message: joined?.name ? `Du er nu med i «${joined.name}»` : 'Du er nu medlem af gruppen',
        type: 'success',
      })
      setLeagueForm((p) => ({ ...p, code: '' }))
      setLeagueModalOpen(false)
      if (joined?.id) setSelectedLeagueId(joined.id)
      await refetchLeagues()
    },
    onError: (e) => setToast({ open: true, message: e.message || 'Kunne ikke join gruppe', type: 'error' }),
  })
  const [leaveLeague, { loading: leavingLeague }] = useMutation(LEAVE_WORLD_CUP_LEAGUE, {
    onCompleted: async () => {
      setLeaveLeagueConfirm(null)
      setToast({ open: true, message: 'Du har forladt gruppen', type: 'success' })
      await refetchLeagues()
    },
    onError: (e) => setToast({ open: true, message: e.message || 'Kunne ikke forlade gruppe', type: 'error' }),
  })

  const openGuess = (match) => {
    setForm({ homeGoals: '', awayGoals: '', isJoker: false })
    setJokerInfoOpen(false)
    setGuessModal({ open: true, match })
  }

  const saveGuess = async () => {
    const h = Number(form.homeGoals)
    const a = Number(form.awayGoals)
    if (!Number.isInteger(h) || h < 0 || !Number.isInteger(a) || a < 0) {
      setToast({ open: true, message: 'Udfyld gyldige mål (heltal >= 0)', type: 'error' })
      return
    }
    await createGuess({
      variables: {
        input: {
          matchId: guessModal.match.id,
          homeGoals: h,
          awayGoals: a,
          isJoker: form.isJoker,
        },
      },
    })
  }

  const currentTime = Date.now()
  const nextMatch = matches.find((m) => new Date(m.kickoffTime).getTime() > currentTime) || null
  const firstMatchKickoff = useMemo(() => {
    if (!matches.length) return null
    return matches.reduce((earliest, match) => {
      const kickoffMs = new Date(match.kickoffTime).getTime()
      if (!Number.isFinite(kickoffMs)) return earliest
      if (!earliest || kickoffMs < earliest.kickoffMs) {
        return { kickoffMs, kickoffTime: match.kickoffTime }
      }
      return earliest
    }, null)
  }, [matches])
  const firstMatchKickoffMs = firstMatchKickoff?.kickoffMs ?? null
  const championGuessOpen = firstMatchKickoffMs === null || currentTime < firstMatchKickoffMs
  const championGuessLocked = !championGuessOpen || !!myChampionGuess?.locked
  const championGuessesVisible = firstMatchKickoffMs !== null && currentTime >= firstMatchKickoffMs
  const firstMatchKickoffLabel = firstMatchKickoff?.kickoffTime ? fmtKickoffDate(firstMatchKickoff.kickoffTime) : null
  const showChampionForm = !championGuessLocked && (!myChampionGuess || editingChampionGuess)

  const canSeeLeaderboardChampionGuess = (row) => {
    if (!row?.championTeam) return false
    if (row.userId === currentUserId) return true
    return championGuessesVisible
  }

  const openLeaderboardPlayerModal = (row, totalPlayers) => {
    setLeaderboardPlayerModal({ ...row, totalPlayers })
  }

  const startEditingChampionGuess = () => {
    setChampionTeam(myChampionGuess?.championTeam || '')
    setEditingChampionGuess(true)
  }

  const cancelEditingChampionGuess = () => {
    setChampionTeam('')
    setEditingChampionGuess(false)
  }

  const onOpenMatchGuesses = (matchId) => {
    if (selectedMatchId === matchId) {
      setSelectedMatchId(null)
      return
    }
    setSelectedMatchId(matchId)
  }

  const copyLeagueCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code)
      setToast({ open: true, message: 'Invite-kode kopieret', type: 'success' })
    } catch {
      setToast({ open: true, message: 'Kunne ikke kopiere koden', type: 'error' })
    }
  }

  const getLeaderboardRankIcon = (rank, totalPlayers) => {
    if (rank === totalPlayers && totalPlayers > 1) return '💩'
    if (rank === 1) return '🏆'
    if (rank === 2) return '🥈'
    if (rank === 3) return '🥉'
    return rank
  }

  const getLeaderboardRankClass = (rank, totalPlayers) => {
    if (rank === 1) return 'worldcup-leader-row--first'
    if (rank === 2) return 'worldcup-leader-row--second'
    if (rank === 3) return 'worldcup-leader-row--third'
    if (rank === totalPlayers && totalPlayers > 1) return 'worldcup-leader-row--last'
    return 'worldcup-leader-row--regular'
  }

  const renderLeaderboardRows = (rows) => {
    if (!rows.length) {
      return <p className="worldcup-league-empty">Ingen point endnu.</p>
    }

    return (
      <div className="worldcup-leaderboard">
        <div className="worldcup-leaderboard-head" aria-hidden="true">
          <span>Placering</span>
          <span>Navn</span>
          <span>Point</span>
        </div>
        {rows.map((r) => {
          const totalPlayers = rows.length
          return (
            <button
              type="button"
              key={r.userId}
              className={`worldcup-leader-row worldcup-leader-row--clickable ${getLeaderboardRankClass(r.rank, totalPlayers)}`}
              onClick={() => openLeaderboardPlayerModal(r, rows.length)}
            >
              <span className="worldcup-leader-rank">{getLeaderboardRankIcon(r.rank, totalPlayers)}</span>
              <span className="worldcup-leader-name">{r.userName}</span>
              <span className="worldcup-leader-points">
                <span className="worldcup-leader-points-value">{r.totalPoints}</span>
                <span className="worldcup-leader-points-label">point</span>
              </span>
              <span className="worldcup-leader-details">
                Perfekte: {r.perfectPredictions} · Joker: {r.jokerPoints} · VM: {r.worldChampionPoints}
              </span>
            </button>
          )
        })}
      </div>
    )
  }

  const teamsTabLoading = isInitialQueryLoading(teamsLoading, teamsData)
  const matchesTabLoading = isInitialQueryLoading(matchesLoading, matchesData)
    || isInitialQueryLoading(myGuessesLoading, myGuessesData)
    || isInitialQueryLoading(teamsLoading, teamsData)
    || isInitialQueryLoading(stagesLoading, stagesData)
  const myGuessesTabLoading = isInitialQueryLoading(myGuessesLoading, myGuessesData)
    || isInitialQueryLoading(matchesLoading, matchesData)
    || isInitialQueryLoading(teamsLoading, teamsData)
  const leaderboardTabLoading = isInitialQueryLoading(leaderboardLoading, leaderboardData)
    || isInitialQueryLoading(matchesLoading, matchesData)
  const leaguesTabLoading = isInitialQueryLoading(leaguesLoading, leaguesData)
  const worldChampionTabLoading = isInitialQueryLoading(championLoading, championData)
    || isInitialQueryLoading(teamsLoading, teamsData)
    || isInitialQueryLoading(matchesLoading, matchesData)

  const refreshWorldCup = async () => {
    await Promise.all([
      client.query({ query: WORLD_CUP_MATCHES, fetchPolicy: 'network-only' }),
      client.query({ query: MY_WORLD_CUP_GUESSES, fetchPolicy: 'network-only' }),
      client.query({ query: WORLD_CUP_LEADERBOARD, fetchPolicy: 'network-only' }),
      client.query({ query: WORLD_CUP_TEAMS, fetchPolicy: 'network-only' }),
    ])
  }

  return (
    <PullToRefresh onRefresh={refreshWorldCup}>
    <div className="worldcup-root">
      <div className="worldcup-background" aria-hidden="true" />
      <div className="worldcup-page">
        <BackHeader
          className="worldcup-bar"
          theme="worldcup"
          eyebrow="Svirr"
          title="World Cup"
          onBack={() => navigate('/spil')}
          backLabel="Tilbage til spil"
          actions={(
            <>
              <a
                href="https://sport.tv2.dk/fodbold/vm/sendeplan"
                target="_blank"
                rel="noopener noreferrer"
                className="worldcup-header-btn"
              >
                Sendeplan
              </a>
              <button
                type="button"
                className="worldcup-header-btn"
                onClick={() => setRulesOpen(true)}
              >
                Spilregler
              </button>
            </>
          )}
        />
      <div className="worldcup-header">
        {nextMatch ? (
          <div className="worldcup-next">
            <span className="worldcup-next-label">Næste kamp</span>
            <div className="worldcup-next-fixture">
              <span className="worldcup-next-team">
                <span className="worldcup-next-team-name">{nextMatch.homeTeam}</span>
                <WorldCupTeamFlag name={nextMatch.homeTeam} flagCode={flagCodeByName[nextMatch.homeTeam]} />
              </span>
              <span className="worldcup-next-vs">vs</span>
              <span className="worldcup-next-team">
                <WorldCupTeamFlag name={nextMatch.awayTeam} flagCode={flagCodeByName[nextMatch.awayTeam]} />
                <span className="worldcup-next-team-name">{nextMatch.awayTeam}</span>
              </span>
            </div>
            <span className="worldcup-next-meta">{fmtKickoffDate(nextMatch.kickoffTime)} · {worldCupStageLabel(nextMatch.stage)}</span>
          </div>
        ) : null}
      </div>

      <div className="worldcup-tabs" ref={tabsRef}>
        {tabs.map((tab) => (
          <button
            type="button"
            key={tab.key}
            className={activeTab === tab.key ? 'active' : ''}
            onClick={() => navigate(tab.path)}
          >
            <span className="worldcup-tab-label">{tab.label}</span>
            {tab.key === 'matches' && missingGuessSummary.totalMissing > 0 ? (
              <span
                className={`worldcup-tab-badge${missingGuessSummary.isUrgent ? ' worldcup-tab-badge--urgent' : ''}`}
                aria-label={`${missingGuessSummary.totalMissing} kampe mangler gæt`}
              >
                {missingGuessSummary.totalMissing}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {missingGuessSummary.bannerText ? (
        <div
          className={`worldcup-missing-banner${missingGuessSummary.isUrgent ? ' worldcup-missing-banner--urgent' : ''}`}
          role="status"
        >
          <div className="worldcup-missing-banner__content">
            <span className="worldcup-missing-banner__icon" aria-hidden="true">!</span>
            <p className="worldcup-missing-banner__text">{missingGuessSummary.bannerText}</p>
          </div>
          <Button
            title="Gå til kampe"
            variant="outlined"
            className="worldcup-missing-banner__btn"
            onClick={() => navigate('/world-cup-2026/matches')}
          />
        </div>
      ) : null}

      {activeTab === 'teams' && (
        teamsError && !teamsData ? (
          <Card className="worldcup-panel-card worldcup-tab-error" hover={false}>
            <p>Kunne ikke hente hold. Tjek forbindelsen og prøv igen.</p>
            <Button title="Prøv igen" onClick={() => refetchTeams()} />
          </Card>
        ) : teamsTabLoading ? (
          <WorldCupTabLoading text="Henter hold…" />
        ) : (
          <WorldCupGroupsView teams={teams} />
        )
      )}

      {activeTab === 'matches' && (
        matchesTabLoading ? (
          <WorldCupTabLoading text="Henter kampe…" />
        ) : (
          <WorldCupMatchesView
            matches={matches}
            stages={stages}
            teams={teams}
            myGuessByMatch={myGuessByMatch}
            selectedMatchId={selectedMatchId}
            selectedMatchGuesses={selectedMatchGuesses}
            onOpenGuess={openGuess}
            onOpenMatchGuesses={onOpenMatchGuesses}
          />
        )
      )}

      {activeTab === 'my-guesses' && (
        myGuessesTabLoading ? (
          <WorldCupTabLoading text="Henter dine gæt…" />
        ) : (
        <Card className="worldcup-panel-card worldcup-my-guesses-panel" hover={false}>
          <WorldCupMyGuessesView
            myGuesses={myGuesses}
            matches={matches}
            flagCodeByName={flagCodeByName}
            jokerUsed={jokerUsed}
            onGoToMatches={() => navigate('/world-cup-2026/matches')}
          />
        </Card>
        )
      )}

      {activeTab === 'leaderboard' && (
        leaderboardTabLoading ? (
          <WorldCupTabLoading text="Henter leaderboard…" />
        ) : (
        <Card className="worldcup-panel-card" hover={false}>
          <div className="worldcup-panel-header">
            <h3>Globalt leaderboard</h3>
            <WorldCupLeaderboardRefreshButton onRefresh={refetchLeaderboard} />
          </div>
          {renderLeaderboardRows(leaderboard)}
        </Card>
        )
      )}

      {activeTab === 'leagues' && (
        leaguesTabLoading ? (
          <WorldCupTabLoading text="Henter grupper…" />
        ) : (
        <Card className="worldcup-panel-card worldcup-leagues-panel" hover={false}>
          <div className="worldcup-league-toolbar">
            {activeLeague ? (
              <button
                type="button"
                className="worldcup-league-code-btn"
                aria-label={`Kopier gruppekode ${activeLeague.inviteCode}`}
                title={`Kopier kode: ${activeLeague.inviteCode}`}
                onClick={() => copyLeagueCode(activeLeague.inviteCode)}
              >
                <span className="worldcup-league-code-btn-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" focusable="false">
                    <path
                      d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 18H8V7h11v16z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span className="worldcup-league-code-btn-content">
                  <span className="worldcup-league-code-btn-text">Kopier gruppekode</span>
                  <span className="worldcup-league-code-btn-code">{activeLeague.inviteCode}</span>
                </span>
              </button>
            ) : (
              <span className="worldcup-league-toolbar-spacer" aria-hidden="true" />
            )}
            <Button
              title="Opret/Join"
              variant="outlined"
              className="worldcup-league-manage-btn"
              onClick={() => setLeagueModalOpen(true)}
            />
          </div>
          {leagues.length === 0 ? (
            <p className="worldcup-league-empty">Du er ikke medlem af nogen gruppe endnu.</p>
          ) : activeLeague ? (
            <>
              {leagues.length > 1 ? (
                <div className="worldcup-league-tabs" role="tablist" aria-label="Dine grupper">
                  {leagues.map((l) => (
                    <button
                      key={l.id}
                      type="button"
                      role="tab"
                      aria-selected={activeLeague.id === l.id}
                      className={activeLeague.id === l.id ? 'active' : ''}
                      onClick={() => setSelectedLeagueId(l.id)}
                    >
                      {l.name}
                    </button>
                  ))}
                </div>
              ) : null}
              <div className="worldcup-league-block">
                <div className="worldcup-league-leaderboard">
                  <h4>{activeLeague.name} leaderboard</h4>
                  {renderLeaderboardRows(activeLeague.leaderboard || [])}
                </div>
                <div className="worldcup-league-leave-actions">
                  <Button
                    title="Forlad gruppe"
                    variant="outlined"
                    color="danger"
                    onClick={() =>
                      setLeaveLeagueConfirm({ id: activeLeague.id, name: activeLeague.name })
                    }
                  />
                </div>
              </div>
            </>
          ) : null}
        </Card>
        )
      )}

      {activeTab === 'world-champion' && (
        worldChampionTabLoading ? (
          <WorldCupTabLoading text="Henter verdensmester…" />
        ) : (
        <Card className="worldcup-panel-card" hover={false}>
          <h3>Verdensmester-gæt</h3>
          {firstMatchKickoffLabel ? (
            <p className="worldcup-champion-deadline">
              {championGuessOpen
                ? `Du kan gætte/ændre dit verdensmester-gæt indtil første kamp starter (${firstMatchKickoffLabel}).`
                : `Gættet er låst — første kamp startede ${firstMatchKickoffLabel}.`}
            </p>
          ) : (
            <p className="worldcup-champion-deadline">Du kan gætte dit verdensmester-gæt, indtil den første kamp er sat op og starter.</p>
          )}
          {myChampionGuess && championGuessOpen ? (
            <p className="worldcup-champion-hint">
              Et rigtigt gæt giver {WORLD_CHAMPION_POINTS} point.
            </p>
          ) : null}
          {myChampionGuess && championGuessLocked ? (
            <p className="worldcup-champion-hint">
              Et rigtigt gæt giver {WORLD_CHAMPION_POINTS} point.
            </p>
          ) : null}
          {myChampionGuess ? (
            <div className="worldcup-champion-selected">
              <div className="worldcup-champion-selected-main">
                <span className="worldcup-champion-selected-label">Dit valg</span>
                <div className="worldcup-champion-selected-team-row">
                  <WorldCupTeamFlag
                    name={myChampionGuess.championTeam}
                    flagCode={flagCodeByName[myChampionGuess.championTeam]}
                    className="worldcup-champion-flag"
                  />
                  <strong className="worldcup-champion-selected-team">{myChampionGuess.championTeam}</strong>
                </div>
              </div>
              <span className="worldcup-champion-selected-meta">
                {myChampionGuess.pointsAwarded} point · {championGuessLocked ? 'Låst' : 'Åben'}
              </span>
            </div>
          ) : championGuessLocked ? (
            <div>Du nåede ikke at vælge verdensmester, før første kamp startede.</div>
          ) : (
            <div>Du har ikke valgt verdensmester endnu.</div>
          )}
          {myChampionGuess && !championGuessLocked && !editingChampionGuess ? (
            <div className="worldcup-champion-actions">
              <Button title="Ændre gæt" variant="outlined" onClick={startEditingChampionGuess} />
            </div>
          ) : null}
          {showChampionForm ? (
            <div className="worldcup-league-actions worldcup-champion-form">
              <ComboInput
                id="wc-user-champion"
                value={championTeam}
                options={teamOptions}
                placeholder="Vælg verdensmester"
                ariaLabel="Verdensmester"
                onChange={setChampionTeam}
              />
              <Button
                title="Gem verdensmester"
                color="confirmation"
                className="worldcup-save-guess-btn"
                onClick={() => saveChampion({ variables: { championTeam } })}
                loading={savingChampion}
              />
              {myChampionGuess && editingChampionGuess ? (
                <Button title="Annuller" variant="outlined" color="danger" onClick={cancelEditingChampionGuess} />
              ) : null}
            </div>
          ) : null}
        </Card>
        )
      )}

      <Modal
        isOpen={!!leaderboardPlayerModal}
        onClose={() => setLeaderboardPlayerModal(null)}
        title={leaderboardPlayerModal ? leaderboardPlayerModal.userName : 'Spiller'}
        size="small"
        className="worldcup-modal worldcup-leaderboard-player-modal-shell"
      >
        {leaderboardPlayerModal ? (
          <div className="worldcup-leaderboard-player-modal">
            <div
              className={`worldcup-leaderboard-player-hero ${getLeaderboardRankClass(
                leaderboardPlayerModal.rank,
                leaderboardPlayerModal.totalPlayers || 1
              )}`}
            >
              <span className="worldcup-leaderboard-player-hero-rank" aria-hidden="true">
                {getLeaderboardRankIcon(
                  leaderboardPlayerModal.rank,
                  leaderboardPlayerModal.totalPlayers || 1
                )}
              </span>
              <div className="worldcup-leaderboard-player-hero-copy">
                <span className="worldcup-leaderboard-player-hero-label">Placering #{leaderboardPlayerModal.rank}</span>
                <span className="worldcup-leaderboard-player-hero-total">{leaderboardPlayerModal.totalPoints}</span>
                <span className="worldcup-leaderboard-player-hero-total-label">point i alt</span>
              </div>
            </div>

            <div className="worldcup-leaderboard-player-metrics">
              <div className="worldcup-leaderboard-player-metric">
                <span className="worldcup-leaderboard-player-metric-icon" aria-hidden="true">🎯</span>
                <span className="worldcup-leaderboard-player-metric-value">{leaderboardPlayerModal.perfectPredictions}</span>
                <span className="worldcup-leaderboard-player-metric-label">Ramte plet</span>
              </div>
              <div className="worldcup-leaderboard-player-metric">
                <span className="worldcup-leaderboard-player-metric-icon" aria-hidden="true">🃏</span>
                <span className="worldcup-leaderboard-player-metric-value">{leaderboardPlayerModal.jokerPoints}</span>
                <span className="worldcup-leaderboard-player-metric-label">Joker-point</span>
              </div>
            </div>

            <section className="worldcup-leaderboard-player-champion">
              <div className="worldcup-leaderboard-player-champion-head">
                <h4 className="worldcup-leaderboard-player-section-title">Verdensmester-gæt</h4>
                <span className="worldcup-leaderboard-player-champion-points">
                  {leaderboardPlayerModal.worldChampionPoints} point
                </span>
              </div>
              {!leaderboardPlayerModal.championTeam ? (
                <p className="worldcup-leaderboard-champion-empty">Har ikke gættet verdensmester.</p>
              ) : canSeeLeaderboardChampionGuess(leaderboardPlayerModal) ? (
                <div className="worldcup-leaderboard-player-champion-pick">
                  <WorldCupTeamFlag
                    name={leaderboardPlayerModal.championTeam}
                    flagCode={flagCodeByName[leaderboardPlayerModal.championTeam]}
                  />
                  <span className="worldcup-leaderboard-player-champion-team">{leaderboardPlayerModal.championTeam}</span>
                </div>
              ) : (
                <p className="worldcup-leaderboard-champion-empty worldcup-leaderboard-champion-empty--locked">
                  <span className="worldcup-leaderboard-champion-lock" aria-hidden="true">🔒</span>
                  {firstMatchKickoffLabel
                    ? `Gættet vises, når første kamp er startet (${firstMatchKickoffLabel}).`
                    : 'Gættet vises, når første kamp er startet.'}
                </p>
              )}
            </section>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={guessModal.open}
        onClose={() => {
          setGuessModal({ open: false, match: null })
          setJokerInfoOpen(false)
        }}
        title="Afgiv gæt"
        size="small"
        className="worldcup-modal"
      >
        {guessModal.match ? (
          <div className="worldcup-modal-grid">
            <div className="worldcup-modal-score-row">
              <span className="worldcup-modal-team-name worldcup-modal-team-name--home">{guessModal.match.homeTeam}</span>
              <span className="worldcup-modal-team-name worldcup-modal-team-name--away">{guessModal.match.awayTeam}</span>
              <div className="worldcup-modal-team-entry worldcup-modal-team-entry--home">
                <WorldCupTeamFlag name={guessModal.match.homeTeam} flagCode={flagCodeByName[guessModal.match.homeTeam]} />
                <input
                  className="worldcup-input worldcup-input--score"
                  type="number"
                  min={0}
                  value={form.homeGoals}
                  onChange={(e) => setForm((p) => ({ ...p, homeGoals: e.target.value }))}
                  aria-label={`Mål til ${guessModal.match.homeTeam}`}
                />
              </div>
              <span className="worldcup-modal-score-sep" aria-hidden="true">-</span>
              <div className="worldcup-modal-team-entry worldcup-modal-team-entry--away">
                <input
                  className="worldcup-input worldcup-input--score"
                  type="number"
                  min={0}
                  value={form.awayGoals}
                  onChange={(e) => setForm((p) => ({ ...p, awayGoals: e.target.value }))}
                  aria-label={`Mål til ${guessModal.match.awayTeam}`}
                />
                <WorldCupTeamFlag name={guessModal.match.awayTeam} flagCode={flagCodeByName[guessModal.match.awayTeam]} />
              </div>
            </div>
            <div className="worldcup-joker">
              <div className="worldcup-joker-left">
                <span className="worldcup-joker-label">Brug joker ({jokerUsed}/3 brugt)</span>
                <button
                  type="button"
                  className="worldcup-joker-info-btn"
                  aria-label="Information om jokere"
                  aria-expanded={jokerInfoOpen}
                  onClick={() => setJokerInfoOpen((open) => !open)}
                >
                  i
                </button>
              </div>
              <Switch
                checked={form.isJoker}
                disabled={jokerUsed >= 3}
                onChange={(checked) => setForm((p) => ({ ...p, isJoker: checked }))}
                color="confirmation"
                ariaLabel="Brug joker"
              />
            </div>
            {jokerInfoOpen ? (
              <p className="worldcup-joker-info">
                Gennem hele spillet har du kun 3 jokere, som du kan bruge, hvis du føler dig sikker på et resultat — og få dobbelt op på point.
              </p>
            ) : null}
            <div className="worldcup-modal-actions">
              <p className="worldcup-modal-notice">OBS du kan ikke ændre dit gæt senere!</p>
              <Button
                title="Gem gæt"
                color="confirmation"
                className="worldcup-save-guess-btn"
                onClick={saveGuess}
                loading={savingGuess}
                disabled={!canSaveGuess}
              />
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={!!leaveLeagueConfirm}
        onClose={() => !leavingLeague && setLeaveLeagueConfirm(null)}
        title="Forlad gruppe?"
        size="small"
        className="worldcup-modal"
      >
        {leaveLeagueConfirm ? (
          <div className="worldcup-leave-league-modal">
            <p className="worldcup-leave-league-text">
              Er du sikker på, at du vil forlade <strong>«{leaveLeagueConfirm.name}»</strong>?
              Du kan altid joine igen med invite-koden.
            </p>
            <div className="worldcup-modal-actions">
              <Button
                title="Annuller"
                variant="outlined"
                disabled={leavingLeague}
                onClick={() => setLeaveLeagueConfirm(null)}
              />
              <Button
                title="Forlad gruppe"
                color="danger"
                loading={leavingLeague}
                onClick={() =>
                  leaveLeague({ variables: { leagueId: leaveLeagueConfirm.id } })
                }
              />
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={leagueModalOpen}
        onClose={() => setLeagueModalOpen(false)}
        title="Grupper"
        size="small"
        className="worldcup-modal"
      >
        <div className="worldcup-league-modal">
          <p className="worldcup-league-intro">
            Opret eller join grupper med venner — du kan være med i flere grupper samtidig. Hver gruppe har sit eget lille leaderboard; point tæller som normalt globalt.
          </p>
          <div className="worldcup-league-actions worldcup-league-modal-section">
            <input
              className="worldcup-input"
              value={leagueForm.name}
              placeholder="Nyt gruppenavn"
              onChange={(e) => setLeagueForm((p) => ({ ...p, name: e.target.value }))}
            />
            <Button
              title="Opret gruppe"
              disabled={!leagueForm.name.trim()}
              onClick={() => createLeague({ variables: { name: leagueForm.name.trim() } })}
            />
          </div>
          <div className="worldcup-league-actions worldcup-league-modal-section">
            <input
              className="worldcup-input"
              value={leagueForm.code}
              placeholder="Invite-kode til anden gruppe"
              onChange={(e) => setLeagueForm((p) => ({ ...p, code: e.target.value }))}
            />
            <Button
              title="Join gruppe"
              disabled={!leagueForm.code.trim()}
              onClick={() => joinLeague({ variables: { inviteCode: leagueForm.code.trim() } })}
            />
          </div>
        </div>
      </Modal>

      <WorldCupRulesModal isOpen={rulesOpen} onClose={() => setRulesOpen(false)} />

      </div>
      <Notification open={toast.open} message={toast.message} type={toast.type} onClose={() => setToast((p) => ({ ...p, open: false }))} />
    </div>
    </PullToRefresh>
  )
}
